// ---------------------------------------------------------------------------
// Project card + portfolio derivations.
//
// §5.8 of the UI improvement recommendations asks every project card to show
// three things prominently: driver, next milestone, next action. Plus a
// portfolio-level RAG rollup at the top so an EA can scan health in seconds.
//
// Kept pure so the logic is tested once and reused by the page and by any
// future briefing surface that wants to lead with "this project is overdue
// on delivering X".
// ---------------------------------------------------------------------------

export interface OutcomeLike {
  id: string;
  title: string;
  status: string;
  targetDate?: string | null;
  completedDate?: string | null;
  displayOrder?: number;
}

export interface WorkItemLike {
  id: string;
  title: string;
  status: string;
  targetDate?: string;
  priority?: string;
  projectId?: string;
}

export interface NextMilestone {
  title: string;
  targetDate: string | null;
  daysUntil: number | null;
  overdue: boolean;
}

export interface NextAction {
  title: string;
  targetDate: string | null;
  priority: string | null;
}

function daysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

// Returns the next milestone — the earliest un-achieved outcome with a
// target date. If none have dates, fall back to the first un-achieved
// outcome by displayOrder, so the card still shows *something* meaningful.
export function selectNextMilestone(
  outcomes: OutcomeLike[],
  today: Date = new Date(),
): NextMilestone | null {
  const open = outcomes.filter((o) => o.status !== 'Achieved' && o.status !== 'Cancelled');
  if (open.length === 0) return null;

  const todayMidnight = new Date(today);
  todayMidnight.setHours(0, 0, 0, 0);

  const dated = open
    .filter((o) => !!o.targetDate)
    .map((o) => ({ o, d: new Date(o.targetDate as string) }))
    .filter((x) => !Number.isNaN(x.d.getTime()))
    .sort((a, b) => a.d.getTime() - b.d.getTime());

  if (dated.length > 0) {
    const { o, d } = dated[0];
    d.setHours(0, 0, 0, 0);
    const days = daysBetween(todayMidnight, d);
    return {
      title: o.title,
      targetDate: o.targetDate ?? null,
      daysUntil: days,
      overdue: days < 0,
    };
  }

  const fallback = [...open].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  )[0];
  return { title: fallback.title, targetDate: null, daysUntil: null, overdue: false };
}

const PRIORITY_WEIGHT: Record<string, number> = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3,
};

// Returns the single next action on a project: an open work item, preferring
// the earliest dated one, tie-broken by priority. This is the "what happens
// next if nothing else changes" prompt the EA is scanning for.
export function selectNextAction(
  items: WorkItemLike[],
): NextAction | null {
  const open = items.filter((w) => w.status !== 'Done' && w.status !== 'Cancelled' && w.status !== 'Deferred');
  if (open.length === 0) return null;

  const sorted = [...open].sort((a, b) => {
    const ad = a.targetDate ? new Date(a.targetDate).getTime() : Number.POSITIVE_INFINITY;
    const bd = b.targetDate ? new Date(b.targetDate).getTime() : Number.POSITIVE_INFINITY;
    if (ad !== bd) return ad - bd;
    const ap = PRIORITY_WEIGHT[a.priority ?? 'Medium'] ?? 2;
    const bp = PRIORITY_WEIGHT[b.priority ?? 'Medium'] ?? 2;
    return ap - bp;
  });
  const next = sorted[0];
  return {
    title: next.title,
    targetDate: next.targetDate ?? null,
    priority: next.priority ?? null,
  };
}

export interface RAGRollup {
  red: number;
  amber: number;
  green: number;
  total: number;
  healthPercent: number; // green / total, clamped 0..100
}

export function rollupRag(projects: { ragStatus?: string; isArchived?: boolean; status?: string }[]): RAGRollup {
  const active = projects.filter((p) => !p.isArchived && p.status !== 'Cancelled' && p.status !== 'Completed');
  let red = 0, amber = 0, green = 0;
  for (const p of active) {
    switch (p.ragStatus) {
      case 'Red': red++; break;
      case 'Amber': amber++; break;
      case 'Green':
      default: green++; break;
    }
  }
  const total = active.length;
  const healthPercent = total === 0 ? 0 : Math.round((green / total) * 100);
  return { red, amber, green, total, healthPercent };
}

export function describeMilestone(m: NextMilestone | null): string | null {
  if (!m) return null;
  if (m.daysUntil === null) return `Next milestone: ${m.title}`;
  if (m.overdue) return `Next milestone: ${m.title} (${Math.abs(m.daysUntil)} days overdue)`;
  if (m.daysUntil === 0) return `Next milestone: ${m.title} (due today)`;
  if (m.daysUntil <= 7) return `Next milestone: ${m.title} (${m.daysUntil} days)`;
  return `Next milestone: ${m.title}`;
}
